import { useEffect } from "react";

import { Nav, Footer } from "../components/Nav";
import cover from "../images/SMT/SMT Data Page.png";
import colorImg from "../images/SMT/SMT Colors.png";
import fontImg from "../images/SMT/SMT Fonts.png";
import popupImg from "../images/SMT/SMT Popup Page.png";
import searchImg from "../images/SMT/SMT Search Page.png";
import startImg from "../images/SMT/SMT Start Page.png";
import playerImg from "../images/SMT/SMT Player Page.png";

import emptyImg from "../images/SMT/SMT Empty Page.png";
import dataImg from "../images/SMT/SMT Data Page.png";
import finalImg from "../images/SMT/SMT Final.png";
import {
  ProjectInfo,
  BigStatement,
  TextSection,
  MoreSection,
} from "../components/ProjectDetails";

export default function SMT() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <header id="project-cover">
        <Nav color="white" />

        <h1>Who's on First?</h1>
        <p>
          Ranked 6th Place for the SportsMedia Technology (SMT) Data Challenge
          <br />A customizable baseball defense simulator designed for coaches
          and players to optimize their lineup.
        </p>
        <img id="cover" src={cover} alt="SMT Mockup" />
      </header>

      <main id="project-detail-main">
        <ProjectInfo
          role={["UI Designer", "Front-end Developer"]}
          team={["2 Members", "Jared Maeyama"]}
          toolkit={["Figma", "React", "Flask"]}
          duration="2 Months (2024)"
        />
        <BigStatement header="Big Question">
          How can we help baseball coaches and players optimize their defensive
          lineup more effectively?
        </BigStatement>
        <TextSection type="callout" header="Solution" title="Who's on First?">
          <p>
            A web-based baseball defense simulator application that assesses
            individual and team defense based on player positions and overall
            lineup dynamics.
          </p>
          <a
            className="primary-button"
            href="https://github.com/tsaichenlin/smt"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginRight: "1rem" }}
          >
            View Code on GitHub
          </a>
          <a
            className="primary-button"
            href="https://www.figma.com/design/BB6GQ8hi2QngejcTgcP68k/Mandy-Lin's-Projects?node-id=181-84"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Design in Figma
          </a>
          <h1>My Key Contributions</h1>
          <ul>
            <li>Designed the user interface.</li>
            <li>
              Built a dynamic web application and preprocessed user inputs
              before passing data to the model.
            </li>
            <li>
              Integrated the front-end application with the back-end Flask
              server.
            </li>
          </ul>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Brainstorming"
          title="Narrowing the Scope: Focusing on Team Defense"
        >
          <p>
            For this data challenge, we were given raw data and an open-ended
            prompt. After extensive brainstorming, we decided to focus on
            evaluating team defense holistically, considering how each player's
            stats influence their teammates' performance. Additionally, we aimed
            to create a user-friendly application tailored for coaches, players,
            and teams.
          </p>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Planning"
          title="Creating A Minimum Viable Product"
        >
          <p>
            Below is our Minimum Viable Product (MVP), which guided our design
            and development process.
          </p>
          <p>
            <strong>Model</strong>
          </p>
          <ul>
            <li>
              Generates defense values based on the selected players and their
              positions.
            </li>
            <li>
              Pre-processing includes rough calculations of outs and innings to
              train the model and calculate runs.
            </li>
          </ul>
          <p>
            <strong>Web Application</strong>
          </p>
          <ul>
            <li>
              Allows users to customize the team lineup with features such as:
            </li>
            <ul>
              <li>Adding, removing, and editing players.</li>
              <li>Searching for players to include in the lineup.</li>
            </ul>
            <li>
              Enables users to simulate the lineup using the model and view
              resulting defense metrics.
            </li>
            <li>
              Provides an evaluation report with detailed defense results.
            </li>
            <li>
              Supports iterative improvements, allowing users to modify the
              lineup and resimulate based on the model’s feedback.
            </li>
          </ul>
        </TextSection>
        <TextSection type="normal-text" header="Design " title="Color Palette">
          <p>
            To design a UI that users can instantly associate with baseball, I
            chose colors that are reminiscent of those in the Major and Minor
            League Baseball logos.
          </p>
          <img src={colorImg} alt="SMT Color Palette" />
        </TextSection>{" "}
        <TextSection type="normal-text" header="Design" title="Typography">
          <p>
            To prevent the page from becoming cluttered when presenting large
            amounts of data, I selected a simple and modern font to maintain a
            clean look.
          </p>
          <img src={fontImg} alt="SMT Fonts" />
        </TextSection>
        <TextSection
          type="normal-text"
          header="Development"
          title="Using React and Flask to Build the Web App"
        >
          <ul>
            <li>
              We chose React for the front-end due to its component-based
              architecture, which enabled us to build the application more
              quickly and efficiently.
            </li>
            <li>
              We used Flask for the back-end as it provided a lightweight and
              straightforward server to run the model.
            </li>
          </ul>
        </TextSection>
        <TextSection type="callout" header="Final Product" title="Start Page">
          <p>
            The start page captures the feeling of entering a baseball field
            with a photo of Dodger Stadium and a blue translucent overlay.{" "}
          </p>
          <img src={startImg} alt="SMT Start Page" />
          <p>
            *Note: The final mockups are created using screenshots from the web
            applications.
          </p>
        </TextSection>
        <TextSection
          type="callout"
          header="Final Product"
          title="Popup Tutorial"
        >
          <p>
            A simple pop-up tutorial helps users get started and can be accessed
            anytime from the simulation page.
          </p>
          <img src={popupImg} alt="SMT Popup Page" />
        </TextSection>
        <TextSection
          type="callout"
          header="Final Product"
          title="Simulation Page"
        >
          <p>
            A simple three-column layout features an interactive baseball field,
            player lineup, and simulation reports, allowing users to quickly
            edit the lineup, run simulations, and view results—all on a single
            page for quick iterations and easy navigation.
          </p>
          <img src={emptyImg} alt="SMT Simulation Page" />
        </TextSection>
        <TextSection
          type="callout"
          header="Final Product"
          title="Search Feature"
        >
          <p>
            Easily add or edit players in the lineup by searching for their
            name, ID, or position. Basic statistics are provided to assist in
            making strategic lineup decisions.
          </p>
          <img src={searchImg} alt="SMT Search Page" />
        </TextSection>
        <TextSection
          type="callout"
          header="Final Product"
          title="View Player Stats"
        >
          <p>
            The interactive baseball field and lineup buttons allow users to
            quickly view the stats of a selected player in a specific position.
          </p>
          <img src={playerImg} alt="SMT Player Stats Page" />
        </TextSection>{" "}
        <TextSection
          type="callout"
          header="Final Product"
          title="Evaluation Report"
        >
          <p>
            After selecting a lineup and running a simulation with our model,
            view a comprehensive report of the lineup’s defensive evaluation.
          </p>
          <img src={dataImg} alt="SMT Data Page" />
        </TextSection>
        <TextSection
          type="normal-text"
          header="Final Product"
          title="All Pages"
        >
          <img
            src={finalImg}
            className="full-width-img"
            alt="SMT All Pages Poster"
          />
        </TextSection>
      </main>
      <MoreSection exclude={["smt"]} />

      <Footer />
    </>
  );
}
